import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { match } from 'path-to-regexp';
import { environment } from '@environments/environment';
import { MICROSERVICES } from '@shared/constants/microservices';
import { MOCK_RULES } from '../api/mock-api.config';

interface MockMatch {
    serviceKey: keyof typeof MICROSERVICES;
    path: string;
    header?: string;
}

const findMockMatch = (url: string, method: string): MockMatch | null => {
    for (const rule of MOCK_RULES) {
        const servicePath = MICROSERVICES[rule.serviceKey];
        const realBase = `${environment.url}${servicePath}`;
        const mockBase = `${environment.mockBaseUrl}${servicePath}`;

        const matchResult = matchPath(url, realBase, rule.paths, method) ?? matchPath(url, mockBase, rule.paths, method);
        if (matchResult) {
            return { serviceKey: rule.serviceKey, path: matchResult.path, header: matchResult.header };
        }
    }

    return null;
};

const matchPath = (
    url: string,
    base: string,
    paths: { path: string; method?: string; header?: string }[],
    method: string
): { path: string; header?: string } | null => {
    if (!url.startsWith(base)) {
        return null;
    }

    const path = url.substring(base.length);
    const pathRule = paths.find((rule) => {
        const matcher = match(rule.path, { decode: decodeURIComponent, end: true });
        const pathMatches = matcher(path) !== false;
        const methodMatches = !rule.method || rule.method === method;
        return pathMatches && methodMatches;
    });

    if (!pathRule) {
        return null;
    }

    return { path, header: pathRule.header };
};

export const mockRoutingInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    if (!environment.useMocks) {
        return next(req);
    }

    const mockMatch = findMockMatch(req.url, req.method);

    if (!mockMatch) {
        return next(req);
    }

    let headers = req.headers;
    if (mockMatch.header) {
        headers = headers.set('Prefer', mockMatch.header);
    }

    const mockBaseUrl = `${environment.mockBaseUrl}${MICROSERVICES[mockMatch.serviceKey]}`;
    const url = req.url.startsWith(mockBaseUrl) ? req.url : `${mockBaseUrl}${mockMatch.path}`;

    console.log(`[MOCK ROUTING] ${req.method} ${req.url} -> ${url}`);

    return next(req.clone({ url, headers }));
};
